import React from 'react';
import { ENTITY_TYPES } from '@adsk/bim360-matrix-react-entity-linker';
import handleFetchErrors from '@adsk/bim360-matrix-react-helpers/es/handleFetchErrors';
import { ISSUES_ENV_CONFIG_KEYS } from './consts';
import IssueEntityBlock from './IssueEntityBlock';
import IssueEntityPickerItem from './IssueEntityPickerItem';
import formatIssueName from './helpers/formatIssueName';
import getEnvConfig from './helpers/getEnvConfig';
import IssueLinkedEntitiesCellItem from './IssueLinkedEntitiesCellItem';
import IssueLinkedEntitiesCellBlock from './IssueLinkedEntitiesCellBlock';
import getNextPageQueryHelper from './helpers/getNextPageQueryHelper';
import IssueCreateNewEntityForm from './IssueCreateNewEntityForm';
import { canCreate } from './helpers/permissionHelper';
import validate from './helpers/validationHelper';

const WILD_CARD_OPERAND = '%';

export default {
  entityType: ENTITY_TYPES.ISSUE,
  metadata: {
    containerType: 'issue'
  },
  createPlugin: ({ env, region, projectId, containerId, getToken }) => {
    const apiCache = { [containerId]: null };
    const baseUrl = getEnvConfig(env, region, ISSUES_ENV_CONFIG_KEYS.ISSUES_BE);
    const parseEntity = e => ({ ...e, entityType: ENTITY_TYPES.ISSUE });
    const EntityBlock = props => (
      <IssueEntityBlock {...props} env={env} region={region} projectId={projectId} />
    );

    const LinkedEntitiesCellBlock = props => (
      <IssueLinkedEntitiesCellBlock {...props} env={env} region={region} projectId={projectId} />
    );
    const CreateNewEntityForm = props => (
      <IssueCreateNewEntityForm env={env} region={region} {...props} />
    );

    return {
      queryEntities: query => {
        const pageLimit = query.pageLimit;
        const searchExpression = query.searchExpression;
        const offset = query.offset || 0;
        const searchParams = new URLSearchParams({ sortBy: '-displayId' });

        if (pageLimit) {
          searchParams.set('limit', pageLimit);
        }
        if (offset) {
          searchParams.set('offset', offset);
        }
        if (searchExpression) {
          searchParams.set(
            'filter[search]',
            `${WILD_CARD_OPERAND}${searchExpression}${WILD_CARD_OPERAND}`
          );
        }
        const searchParamsStr = searchParams.toString();
        const queryString = searchParamsStr ? `?${searchParamsStr}` : '';
        return getToken()
          .then(token =>
            fetch(`${baseUrl}/v2/containers/${containerId}/issues${queryString}`, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            })
          )
          .then(handleFetchErrors)
          .then(res => res.json())
          .then(response => {
            return {
              data: response.results.map(parseEntity),
              nextPageQuery: getNextPageQueryHelper(
                query,
                response.pagination.limit ?? pageLimit,
                response.pagination.offset ?? offset,
                response.pagination.totalResults
              )
            };
          });
      },
      getEntitiesByIds: entityIds =>
        getToken()
          .then(token =>
            fetch(
              `${baseUrl}/v2/containers/${containerId}/issues?filter[id]=${entityIds.join(',')}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            )
          )
          .then(handleFetchErrors)
          .then(res => res.json())
          .then(response => ({
            data: response.results.map(parseEntity)
          })),
      EntityBlock,
      EntityPickerItem: IssueEntityPickerItem,
      formatEntityDisplayText: formatIssueName,
      LinkedEntitiesCellItem: IssueLinkedEntitiesCellItem,
      LinkedEntitiesCellBlock,
      canCreate: createFormProps => {
        if (!validate(createFormProps)) {
          return Promise.resolve(false);
        }
        if (apiCache[containerId] !== null) {
          return Promise.resolve(apiCache[containerId]);
        }
        return getToken()
          .then(token =>
            fetch(`${baseUrl}/v2/containers/${containerId}/users/me`, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            })
          )
          .then(handleFetchErrors)
          .then(res => res.json())
          .then(response => canCreate(response?.permissionLevels ?? []))
          .then(value => {
            apiCache[containerId] = value;
            return value;
          })
          .catch(() => false);
      },
      CreateNewEntityForm,
      clearCache: () => {
        apiCache[containerId] = null;
      }
    };
  }
};
